import React, { useState } from 'react';
import { useUnifiedElementsStore } from '../../stores/unifiedElementsStore';
import { useUIStore } from '../../stores';
import { getCategoryColor } from '../../config/categories';
import { logger } from '../../../../shared/utils/logger';
import './ExportPanel.css';
import { MapService } from '../../services/map/mapService';
import { useCategoriesStore } from '../../stores/categoriesStore';

interface ExportPanelProps {
  isOpen: boolean;
  onClose: () => void;
  mapService?: MapService | null;
}

const ExportPanel: React.FC<ExportPanelProps> = ({ isOpen, onClose, mapService }) => {
  const { addNotification } = useUIStore();
  const { elements } = useUnifiedElementsStore();
  const [selectedFormat, setSelectedFormat] = useState<string>('geojson');
  const [exporting, setExporting] = useState(false);
  const kmlElevationType: 'relativeToGround' = 'relativeToGround';

  const exportFormats = [
    { id: 'geojson', name: 'GeoJSON', description: 'Formato estándar para datos geográficos' },
    { id: 'csv', name: 'CSV', description: 'Archivo de valores separados por punto y coma' },
    { id: 'kml', name: 'KML', description: 'Formato para Google Earth y aplicaciones GIS' }
  ];

  const handleExport = async () => {
    if (elements.length === 0) {
      addNotification({
        type: 'warning',
        title: 'Sin elementos',
        message: 'No hay elementos para exportar'
      });
      return;
    }

    setExporting(true);
    try {
      switch (selectedFormat) {
        case 'geojson':
          await exportToGeoJSON();
          break;
        case 'csv':
          await exportToCSV();
          break;
        case 'kml':
          await exportToKML();
          break;
        default:
          throw new Error('Formato no soportado');
      }
    } catch (error) {
      console.error('Error al exportar:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al exportar los datos'
      });
    } finally {
      setExporting(false);
    }
  };

  const exportToGeoJSON = async () => {
    try {
      logger.log('EXPORT_PANEL', '🔍 Iniciando exportación a GeoJSON...');
      
      // Función para obtener el nombre de la planta
      const getStoreyName = (storeyId: string): string => {
        if (!mapService) return storeyId || '';
        
        const gui = mapService.getGUI();
        if (!gui || !gui.getStoreyDropdownOptions) return storeyId || '';
        
        const storeyOptions = gui.getStoreyDropdownOptions();
        const storey = storeyOptions.find(option => option.id === storeyId);
        return storey?.name || storeyId || '';
      };
      
      // Crear un GeoJSON estándar compatible con QGIS
      const geojsonFeatures = elements.map(element => {
        if (!element.geometry) {
          console.warn(`⚠️ Elemento ${element.id} sin geometría, saltando...`);
          return null;
        }

        // Convertir geometría a WGS84 para GeoJSON
        const geom = element.geometry.clone();
        geom.transform('EPSG:3857', 'EPSG:4326');
        const coordinates = geom.getCoordinates();

        return {
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: coordinates
          },
          properties: {
            id: element.id,
            nombre: element.nombre || 'sin_nombre',
            categoria: element.category,
            finca: element.finca || 'Sin asignar',
            planta: element.storeyId || '',
            nombre_planta: getStoreyName(element.storeyId || ''),
            area_m2: element.area?.toFixed(2) || '0',
            computo: element.computo || 0,
            refcat: element.refcat || '',
            fecha_creacion: element.createdAt,
            fecha_modificacion: element.updatedAt
          }
        };
      }).filter(feature => feature !== null);

      const geojsonDocument = {
        type: "FeatureCollection",
        features: geojsonFeatures,
        properties: {
          nombre: "Elementos del Proyecto",
          fecha_exportacion: new Date().toISOString(),
          total_elementos: geojsonFeatures.length,
          plantas: [...new Set(elements.map(e => e.storeyId))].length
        }
      };

      // Crear el archivo GeoJSON
      const dataStr = JSON.stringify(geojsonDocument, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/geo+json' });

      // Obtener nombre base a partir del IFC cargado si existe
      let fileName = 'elementos';
      try {
        const gui = mapService?.getGUI();
        if (gui && (gui as any).getIfcFileName) {
          const ifcFileName = (gui as any).getIfcFileName();
          if (ifcFileName && ifcFileName.trim() !== '') {
            fileName = ifcFileName.replace(/[^a-zA-Z0-9_-]/g, '_');
          }
        }
      } catch {}

      // Descargar
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.geojson`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

              logger.log('EXPORT_PANEL', `✅ GeoJSON exportado: ${geojsonFeatures.length} elementos`);
      addNotification({
        type: 'success',
        title: 'Exportación completada',
        message: `Archivo GeoJSON exportado: ${geojsonFeatures.length} elementos`
      });
      
    } catch (error) {
      console.error("❌ Error al exportar GeoJSON:", error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al exportar el archivo GeoJSON: ' + (error as Error).message
      });
    }
  };

  const exportToCSV = async () => {
    const headers = ['Nombre', 'Categoría', 'Finca', 'Planta', 'Nombre planta', 'Superficie (m²)', 'Cómputo (%)', 'Ref. Catastral'];
    
    // Función para obtener el nombre de la planta
    const getStoreyName = (storeyId: string): string => {
      if (!mapService) return storeyId || '';
      
      const gui = mapService.getGUI();
      if (!gui || !gui.getStoreyDropdownOptions) return storeyId || '';
      
      const storeyOptions = gui.getStoreyDropdownOptions();
      const storey = storeyOptions.find(option => option.id === storeyId);
      return storey?.name || storeyId || '';
    };
    
    const csvContent = [
      headers.join(';'),
             ...elements.map(element => [
         element.nombre || 'sin_nombre',
         element.category,
         element.finca || 'Sin asignar',
         element.storeyId || '',
         getStoreyName(element.storeyId || ''),
         (element.area?.toFixed(2) || '0').replace('.', ','),
         element.computo || '0',
         element.refcat || ''
       ].join(';'))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `elementos_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    addNotification({
      type: 'success',
      title: 'Exportación completada',
      message: 'Archivo CSV exportado correctamente'
    });
  };

  /**
   * Convierte color CSS (rgba/rgb/hex) a formato KML (aabbggrr)
   */
  const rgbaToKmlColor = (input: string) => {
    if (!input) return 'ff000000';

    // Hex #RRGGBB o #RRGGBBAA o #RGB
    const hexMatch = input.trim().match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/);
    if (hexMatch) {
      let hex = hexMatch[1];
      if (hex.length === 3) {
        // #RGB -> #RRGGBB
        hex = hex.split('').map(c => c + c).join('');
      }
      if (hex.length === 6) {
        const rr = hex.slice(0, 2).toLowerCase();
        const gg = hex.slice(2, 4).toLowerCase();
        const bb = hex.slice(4, 6).toLowerCase();
        const aa = 'ff';
        return `${aa}${bb}${gg}${rr}`;
      }
      if (hex.length === 8) {
        // CSS suele ser #RRGGBBAA
        const rr = hex.slice(0, 2).toLowerCase();
        const gg = hex.slice(2, 4).toLowerCase();
        const bb = hex.slice(4, 6).toLowerCase();
        const aa = hex.slice(6, 8).toLowerCase();
        return `${aa}${bb}${gg}${rr}`;
      }
    }

    // rgba()/rgb()
    const rgbaMatch = input.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/i);
    if (rgbaMatch) {
      const r = Math.max(0, Math.min(255, parseInt(rgbaMatch[1], 10)));
      const g = Math.max(0, Math.min(255, parseInt(rgbaMatch[2], 10)));
      const b = Math.max(0, Math.min(255, parseInt(rgbaMatch[3], 10)));
      const aFloat = rgbaMatch[4] !== undefined ? Math.max(0, Math.min(1, parseFloat(rgbaMatch[4]))) : 1;
      const aHex = Math.round(aFloat * 255).toString(16).padStart(2, '0');
      const bHex = b.toString(16).padStart(2, '0');
      const gHex = g.toString(16).padStart(2, '0');
      const rHex = r.toString(16).padStart(2, '0');
      return `${aHex}${bHex}${gHex}${rHex}`.toLowerCase();
    }

    // Por defecto negro opaco
    return 'ff000000';
  };

  const exportToKML = async () => {
    try {
      logger.log('EXPORT_PANEL', '🔍 Iniciando exportación a KML...');
      
      const kmlStylesMap = new Map<string, string>();
      const kmlFolders: string[] = [];

      // Obtener la elevación base del IFC para usar elevaciones absolutas
      let ifcBaseElevation = 0;
      let ifcElevationType = 'ortometric';
      try {
        // Obtener la elevación desde el ifcModel del GUI (donde se guardan las actualizaciones manuales)
        const gui = mapService?.getGUI();
        if (gui && gui.ifcModel && typeof gui.ifcModel.getElevation === 'function') {
          ifcBaseElevation = gui.ifcModel.getElevation();
          ifcElevationType = gui.ifcModel.getElevationType();
          logger.log('EXPORT_PANEL', `📍 Elevación IFC desde ifcModel del GUI: ${ifcBaseElevation.toFixed(2)} m (${ifcElevationType})`);
        } else {
          // Fallback al IFCService
          try {
            const ifcService = await import('../../services/ifc/ifcService').then(m => m.IFCService.getInstance());
            ifcBaseElevation = ifcService.getModelElevation();
            ifcElevationType = ifcService.getModelElevationType();
            logger.log('EXPORT_PANEL', `📍 Elevación IFC desde IFCService: ${ifcBaseElevation.toFixed(2)} m (${ifcElevationType})`);
          } catch (error) {
            logger.warn('EXPORT_PANEL', '⚠️ No se pudo obtener elevación del IFC, usando 0:', error);
            ifcBaseElevation = 0;
            ifcElevationType = 'ortometric';
          }
        }
      } catch (error) {
        logger.warn('EXPORT_PANEL', '⚠️ No se pudo obtener elevación del IFC, usando 0:', error);
      }

      // Obtener todas las elevaciones de las plantas ordenadas
      const storeyOptions = mapService?.getGUI()?.getStoreyDropdownOptions() || [];
      const storeyElevations = new Map();
      storeyOptions.forEach(({ id }) => {
        const height = mapService?.getGUI()?.getStoreyHeight(id) || 0;
        storeyElevations.set(id, height);
      });

      // Organizar los elementos por planta
      const elementsByFloor: { [key: string]: any[] } = {};
      elements.forEach(element => {
        const storeyId = element.storeyId || 'default';
        if (!elementsByFloor[storeyId]) {
          elementsByFloor[storeyId] = [];
        }
        elementsByFloor[storeyId].push(element);
      });

             // Definir el tipo de elevación para usar en todo el documento
       const elevationTypeText = kmlElevationType === 'absolute' ? 'absoluta' : 'relativa al terreno';
       
       // Crear los placemarks organizados por planta
       Object.entries(elementsByFloor).forEach(([storeyId, floorElements]) => {
         const storeyPlacemarks: string[] = [];
         
         // Obtener la elevación de la planta actual (relativa al IFC)
         const floorRelativeElevation = storeyElevations.get(storeyId) || 0;
         
         // Calcular elevaciones según el tipo seleccionado
         let floorBaseElevation: number;
         let floorTopElevation: number;
         
         if (kmlElevationType === 'absolute') {
           // Elevaciones absolutas sumando la elevación base del IFC
           floorBaseElevation = ifcBaseElevation + floorRelativeElevation;
           
           // Obtener la elevación de la siguiente planta para calcular la altura del techo
           const nextStoreyId = storeyOptions.find((opt, index) => 
             index > storeyOptions.findIndex(s => s.id === storeyId)
           )?.id;
           
           // Si hay una planta siguiente, usar su elevación como techo
           // Si es la última planta, usar la altura configurada por defecto
           const floorTopRelativeElevation = nextStoreyId ? 
             storeyElevations.get(nextStoreyId) : 
             floorRelativeElevation + 3; // Altura por defecto de 3m
           
           floorTopElevation = ifcBaseElevation + floorTopRelativeElevation;
         } else {
           // Elevaciones relativas al terreno (solo las alturas relativas)
           floorBaseElevation = floorRelativeElevation;
           
           // Obtener la elevación de la siguiente planta para calcular la altura del techo
           const nextStoreyId = storeyOptions.find((opt, index) => 
             index > storeyOptions.findIndex(s => s.id === storeyId)
           )?.id;
           
           // Si hay una planta siguiente, usar su elevación como techo
           // Si es la última planta, usar la altura configurada por defecto
           floorTopElevation = nextStoreyId ? 
             storeyElevations.get(nextStoreyId) : 
             floorRelativeElevation + 3; // Altura por defecto de 3m
         }
        logger.log('EXPORT_PANEL', `🏗️ Planta ${storeyId}: Base=${floorBaseElevation.toFixed(2)}m (${elevationTypeText}), Techo=${floorTopElevation.toFixed(2)}m (${elevationTypeText})`);
        logger.log('EXPORT_PANEL', `   Elevación IFC: ${ifcBaseElevation.toFixed(2)}m, Relativa: ${floorRelativeElevation.toFixed(2)}m`);
        logger.log('EXPORT_PANEL', `📊 Elementos en planta ${storeyId}: ${floorElements.length} elementos`);

        floorElements.forEach((element, index) => {
          const type = element.category || 'desconocido';
          const name = element.nombre || type;
          const area = element.area?.toFixed(2) || '0';

          // Obtener color de la categoría del store de categorías
          const categoriesStore = useCategoriesStore.getState();
          const category = categoriesStore.getCategoryById(type);
          const rgba = category ? category.color : 'rgba(255, 87, 34, 0.7)'; // Color por defecto naranja
                      logger.log('EXPORT_PANEL', `🎨 Color para categoría ${type}: ${rgba}`);
          const kmlColor = rgbaToKmlColor(rgba);
                      logger.log('EXPORT_PANEL', `🎨 Color KML para categoría ${type}: ${kmlColor}`);
          const styleId = `style-${type}`;

          if (!kmlStylesMap.has(styleId)) {
            const styleDef = `
            <Style id="${styleId}">
            <PolyStyle>
                <color>${kmlColor}</color>
                <fill>1</fill>
                <outline>1</outline>
            </PolyStyle>
            </Style>`;
            kmlStylesMap.set(styleId, styleDef);
          }

          // Convertir geometría a coordenadas WGS84
          if (element.geometry) {
            const geom = element.geometry.clone();
            geom.transform('EPSG:3857', 'EPSG:4326');
            const coords = geom.getCoordinates()[0]; // Obtener coordenadas del polígono

            // Crear coordenadas para la base y el techo del polígono
            let baseCoords = coords.map(([lon, lat]: [number, number]) => `${lon},${lat},${floorBaseElevation}`);
            let topCoords = coords.map(([lon, lat]: [number, number]) => `${lon},${lat},${floorTopElevation}`);
            
            const elevationTypeText = kmlElevationType === 'absolute' ? 'absoluta' : 'relativa al terreno';
            logger.log('EXPORT_PANEL', `  📍 Elemento ${index + 1} (${type}): usando base=${floorBaseElevation.toFixed(2)}m (${elevationTypeText}), techo=${floorTopElevation.toFixed(2)}m (${elevationTypeText})`);

            // Cerrar los polígonos si es necesario
            if (baseCoords[0] !== baseCoords[baseCoords.length - 1]) {
              baseCoords.push(baseCoords[0]);
            }
            if (topCoords[0] !== topCoords[topCoords.length - 1]) {
              topCoords.push(topCoords[0]);
            }

            // Crear las paredes laterales
            const walls: string[] = [];
            for (let i = 0; i < coords.length - 1; i++) {
              const nextI = (i + 1) % coords.length;
              walls.push(`
                  <LinearRing>
                      <coordinates>
                          ${coords[i][0]},${coords[i][1]},${floorBaseElevation}
                          ${coords[i][0]},${coords[i][1]},${floorTopElevation}
                          ${coords[nextI][0]},${coords[nextI][1]},${floorTopElevation}
                          ${coords[nextI][0]},${coords[nextI][1]},${floorBaseElevation}
                          ${coords[i][0]},${coords[i][1]},${floorBaseElevation}
                      </coordinates>
                  </LinearRing>`);
            }

            const placemark = `
<Placemark>
  <name>${name}</name>
  <styleUrl>#${styleId}</styleUrl>
  <description><![CDATA[
    <b>Tipo:</b> ${type}<br/>
    <b>Planta:</b> ${storeyId}<br/>
    <b>Área:</b> ${area} m²<br/>
    <b>Finca:</b> ${element.finca || 'Sin asignar'}<br/>
    <b>Ref. Catastral:</b> ${element.refcat || 'Sin asignar'}<br/>
    <b>Altura base:</b> ${floorBaseElevation.toFixed(2)} m (${elevationTypeText})<br/>
    <b>Altura techo:</b> ${floorTopElevation.toFixed(2)} m (${elevationTypeText})<br/>
    <b>Elevación IFC:</b> ${ifcBaseElevation.toFixed(2)} m (${ifcElevationType})
  ]]></description>
  <ExtendedData>
    <Data name="tipo"><value>${type}</value></Data>
    <Data name="planta"><value>${storeyId}</value></Data>
    <Data name="area_m2"><value>${area}</value></Data>
    <Data name="finca"><value>${element.finca || 'Sin asignar'}</value></Data>
    <Data name="refcat"><value>${element.refcat || 'Sin asignar'}</value></Data>
    <Data name="altura_base_m"><value>${floorBaseElevation.toFixed(2)}</value></Data>
    <Data name="altura_techo_m"><value>${floorTopElevation.toFixed(2)}</value></Data>
    <Data name="elevacion_ifc_m"><value>${ifcBaseElevation.toFixed(2)}</value></Data>
    <Data name="tipo_elevacion"><value>${ifcElevationType}</value></Data>
    <Data name="altura_relativa_m"><value>${floorRelativeElevation.toFixed(2)}</value></Data>
    <Data name="tipo_altitud_kml"><value>${kmlElevationType}</value></Data>
  </ExtendedData>
  <MultiGeometry>
    <Polygon>
      <altitudeMode>${kmlElevationType}</altitudeMode>
      <outerBoundaryIs>
        <LinearRing>
          <coordinates>${baseCoords.join('\n')}</coordinates>
        </LinearRing>
      </outerBoundaryIs>
    </Polygon>
    <Polygon>
      <altitudeMode>${kmlElevationType}</altitudeMode>
      <outerBoundaryIs>
        <LinearRing>
          <coordinates>${topCoords.join('\n')}</coordinates>
        </LinearRing>
      </outerBoundaryIs>
    </Polygon>
    ${walls.map(wall => `
    <Polygon>
      <altitudeMode>${kmlElevationType}</altitudeMode>
      <outerBoundaryIs>
        ${wall}
      </outerBoundaryIs>
    </Polygon>`).join('\n')}
  </MultiGeometry>
</Placemark>`;

            storeyPlacemarks.push(placemark);
          }
        });

        // Crear la carpeta para esta planta
        const storeyName = storeyOptions.find(opt => opt.id === storeyId)?.name || `Planta ${storeyId}`;
        const folder = `
<Folder>
  <name>${storeyName}</name>
  <description><![CDATA[
    <b>Elevación base:</b> ${floorBaseElevation.toFixed(2)} m (${elevationTypeText})<br/>
    <b>Elevación techo:</b> ${floorTopElevation.toFixed(2)} m (${elevationTypeText})<br/>
    <b>Altura del nivel:</b> ${(floorTopElevation - floorBaseElevation).toFixed(2)} m<br/>
    <b>Elevación IFC:</b> ${ifcBaseElevation.toFixed(2)} m (${ifcElevationType})<br/>
    <b>Elementos:</b> ${floorElements.length}
  ]]></description>
  ${storeyPlacemarks.join('\n')}
</Folder>`;
        kmlFolders.push(folder);
      });

      const allStyles = [...kmlStylesMap.values()].join('\n');

      // Contar total de elementos y plantas
      const totalElements = Object.values(elementsByFloor).reduce((sum, features) => sum + features.length, 0);
      const totalStoreys = Object.keys(elementsByFloor).length;
      
              logger.log('EXPORT_PANEL', `📊 Resumen KML: ${totalStoreys} plantas con ${totalElements} elementos totales`);

      // Obtener nombre del proyecto
      let projectName = "Proyecto BIM";
      try {
        const gui = mapService?.getGUI();
        if (gui && (gui as any).getIfcFileName) {
          const ifcFileName = (gui as any).getIfcFileName();
          if (ifcFileName && ifcFileName.trim() !== '') {
            projectName = ifcFileName.replace(/\.[^/.]+$/, "");
          }
        }
      } catch {}

      const kmlDocument = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
<Document>
  <name>${projectName} - Todas las Plantas</name>
  <description><![CDATA[
    <b>Archivo KML único</b> que contiene todas las plantas del proyecto.<br/>
    <b>Elevaciones ${elevationTypeText}</b> basadas en la elevación del IFC: ${ifcBaseElevation.toFixed(2)} m (${ifcElevationType})<br/>
    <b>Total plantas:</b> ${totalStoreys}<br/>
    <b>Total elementos:</b> ${totalElements}<br/>
    <b>Generado:</b> ${new Date().toLocaleString()}
  ]]></description>
  ${allStyles}
  ${kmlFolders.join('\n')}
</Document>
</kml>`;

      // Crear un nombre de archivo único con timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const fileName = `${projectName}_todas_las_plantas_${timestamp}.kml`;
      
      const blob = new Blob([kmlDocument], { type: "application/vnd.google-earth.kml+xml" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
              logger.log('EXPORT_PANEL', "✅ Archivo KML exportado correctamente:", fileName);
      addNotification({
        type: 'success',
        title: 'Exportación completada',
        message: `Archivo KML exportado: ${fileName} (${totalStoreys} plantas, ${totalElements} elementos, elevaciones ${elevationTypeText})`
      });
      
    } catch (error) {
      console.error("❌ Error al exportar KML:", error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al exportar el archivo KML: ' + (error as Error).message
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="v2-export-panel">
      <div className="v2-export-panel-header">
        <h3 className="v2-export-panel-title">Exportar Elementos</h3>
        <button 
          className="v2-export-panel-close"
          onClick={onClose}
          title="Cerrar"
        >
          ×
        </button>
      </div>

      <div className="v2-export-panel-content">
        <div className="v2-export-info">
          <p>Elementos disponibles para exportar: <strong>{elements.length}</strong></p>
        </div>

        <div className="v2-export-panel-section">
          <h4>Seleccionar formato de exportación:</h4>
          
          {exportFormats.map((format) => (
            <div 
              key={format.id}
              className={`v2-format-option ${selectedFormat === format.id ? 'selected' : ''}`}
              onClick={() => setSelectedFormat(format.id)}
            >
              <div className="v2-format-radio">
                <input
                  type="radio"
                  id={format.id}
                  name="exportFormat"
                  value={format.id}
                  checked={selectedFormat === format.id}
                  onChange={() => setSelectedFormat(format.id)}
                />
                <label htmlFor={format.id}>
                  <span className="v2-format-name">{format.name}</span>
                  <span className="v2-format-description">{format.description}</span>
                </label>
              </div>
            </div>
          ))}
        </div>

        {selectedFormat === 'kml' && (
          <div className="v2-export-panel-section">
            <h4>Configuración de elevación KML:</h4>
            <div className="v2-export-panel-info">
              <ul>
                <li><strong>Relativa al terreno:</strong> Las elevaciones se calculan desde el terreno local usando las alturas relativas de las plantas. Esta es la única opción disponible para exportar KML.</li>
              </ul>
            </div>
          </div>
        )}

        <div className="v2-export-panel-actions">
          <button
            className="v2-export-panel-button"
            onClick={handleExport}
            disabled={exporting || elements.length === 0}
          >
            {exporting ? 'Exportando...' : 'Exportar'}
          </button>
          
          <button
            className="v2-export-panel-button v2-export-panel-button-secondary"
            onClick={onClose}
            disabled={exporting}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportPanel;
